import BACKEND_URL from '../config.js';
const API_URL = BACKEND_URL;

// Evento globale emesso su 403 — Layout lo ascolta per mostrare una notifica
export function dispatch403(message: string) {
  window.dispatchEvent(new CustomEvent('bop:403', { detail: { message } }));
}

export async function apiFetch<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('token');

  // Per FormData non impostare Content-Type: il browser deve aggiungere il boundary automaticamente
  const isFormData = options?.body instanceof FormData;

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });

  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.replace('/');
    throw new Error('Sessione scaduta');
  }

  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { message?: string; error?: string }).message
      ?? (body as { error?: string }).error
      ?? 'Accesso negato';
    dispatch403(msg);
    throw new Error(msg);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error((body as { error?: string }).error ?? `Errore ${res.status}`) as Error & {
      status?: number;
      payload?: unknown;
    };
    err.status = res.status;
    err.payload = body;
    throw err;
  }

  return res.json() as Promise<T>;
}

// Scarica un file binario e lo apre in un nuovo tab (es. PDF inline).
// Accetta opzioni fetch (method, body, headers) per supportare anche POST.
export async function apiOpenBlob(path: string, options?: RequestInit): Promise<void> {
  const token = localStorage.getItem('token');
  const isFormData = options?.body instanceof FormData;
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...(options?.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.replace('/');
    return;
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { message?: string; error?: string }).message
      ?? (body as { error?: string }).error
      ?? 'Accesso negato';
    dispatch403(msg);
    throw new Error(msg);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Errore ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

// Scarica un binario e ne restituisce un object URL, per mostrarlo inline
// (es. <img src=...>). Serve perche' un tag <img> non puo' inviare il Bearer
// token: le risorse protette vanno recuperate via fetch e poi referenziate.
// ⚠️ Il chiamante DEVE fare URL.revokeObjectURL() quando l'elemento sparisce.
export async function apiBlobUrl(path: string, options?: RequestInit): Promise<string> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.replace('/');
    throw new Error('Sessione scaduta');
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { message?: string; error?: string }).message
      ?? (body as { error?: string }).error
      ?? 'Accesso negato';
    dispatch403(msg);
    throw new Error(msg);
  }
  if (!res.ok) throw new Error(`Errore ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

export async function apiDownload(path: string, filename: string, options?: RequestInit): Promise<void> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...(options?.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.replace('/');
    return;
  }
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { message?: string; error?: string }).message
      ?? (body as { error?: string }).error
      ?? 'Accesso negato';
    dispatch403(msg);
    throw new Error(msg);
  }
  if (!res.ok) throw new Error(`Errore ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
